import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';

import { useUser } from '../UserContext';
import { Notification } from 'types/notificationsTypes';

interface Message {
  action: string;
  data: Record<string, any>;
}

interface WebSocketContextProps {
  isConnected: boolean;
  isConnecting: boolean;
  lastMessage: Notification | null;
  sendMessage: (message: Message) => void;
  connect: () => void;
  disconnect: () => void;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  error: string | null;
}

const WebSocketContext = createContext<WebSocketContextProps | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
  wsUrl?: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:80/notifications/ws',
  autoConnect = true,
  reconnectInterval = 5000,
  maxReconnectAttempts = 5,
}) => {
  const { user, token, logout } = useUser();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [lastMessage, setLastMessage] = useState<Notification | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected');
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isManualDisconnectRef = useRef(false);

  const connect = useCallback(() => {
    //console.log('[WebSocket] Connecting to websocket');
    if (!token || !user?.id) {
      console.warn('[WebSocket] Cannot connect: No token or user ID');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      //console.log('[WebSocket] Already connected');
      return;
    }

    if (error) {
      console.warn('[WebSocket] Cannot connect: Error');
      return;
    }

    // setIsConnecting(true);
    // setConnectionStatus('connecting');
    // setError(null);

    try {
      // Create WebSocket connection with authentication
      //console.log('[WebSocket]  Trying to connect to websocket on url:', `${wsUrl}/${user.id}`);
      const ws = new WebSocket(`${wsUrl}/${user.id}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        //console.log('[WebSocket] Connected successfully');
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionStatus('connected');
        setError(null);
        reconnectAttemptsRef.current = 0;

        // Send authentication message
        ws.send(
          JSON.stringify({
            type: 'auth',
            data: {
              userId: user.id,
              token: token,
              userData: {
                id: user.id,
                name: user.first_name + ' ' + user.last_name,
                email: user.email,
              },
            },
            timestamp: new Date().toISOString(),
          })
        );
      };

      ws.onmessage = event => {
        try {
          //console.log('[WebSocket] Received message:', event);
          let message: Notification;
          try {
            message = JSON.parse(event.data);
          } catch {
            // Handle case where event.data is a string
            message = {
              id: uuidv4(),
              title: 'Nova notificação',
              message: event.data,
              is_read: false,
              seen: false,
              read_at: null,
              sent_at: new Date().toISOString(),
              created_at: new Date().toISOString(),
              delivery_method: '',
              origin: '',
              type_id: '',
              user_id: user?.id,
              data: {},
            };
          }
          //console.log('[WebSocket] Received message after parse:', message);

          // Only set lastMessage for actual notifications, not connection/auth messages
          // Check if this is a real notification by looking for specific fields
          const isRealNotification =
            message.title &&
            message.message &&
            message.origin &&
            !message.title.includes('auth') &&
            !message.title.includes('connection');

          if (isRealNotification) {
            setLastMessage(message);
            console.log('[WebSocket] Real notification set:', message);
          } else {
            console.log('[WebSocket] Connection/auth message ignored:', message);
          }

          // // Handle different message types
          // switch (message.type) {
          //     case 'auth_success':
          //         console.log('[WebSocket] Authentication successful');
          //         break;
          //     case 'auth_error':
          //         console.error('[WebSocket] Authentication failed:', message.data);
          //         setError('Authentication failed');
          //         disconnect();
          //         break;
          //     case 'notification':
          //         // Handle notifications
          //         console.log('[WebSocket] Notification received:', message.data);
          //         break;
          //     case 'real_time_update':
          //         // Handle real-time updates
          //         console.log('[WebSocket] Real-time update:', message.data);
          //         break;
          //     case 'error':
          //         console.error('[WebSocket] Server error:', message.data);
          //         setError(message.data.message || 'Server error');
          //         break;
          //     default:
          //         console.log('[WebSocket] Unknown message type:', message.type);
          // }
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error);
        }
      };

      ws.onclose = event => {
        //console.log('[WebSocket] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        setIsConnecting(false);
        setConnectionStatus('disconnected');

        // Handle reconnection logic
        if (!isManualDisconnectRef.current && event.code !== 1000) {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            /* console.log(
              `[WebSocket] Attempting to reconnect... (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`
            ); */
            reconnectAttemptsRef.current++;
            setConnectionStatus('connecting');

            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, reconnectInterval);
          } else {
            console.error('[WebSocket] Max reconnection attempts reached');
            setError('Connection failed after maximum attempts');
            setConnectionStatus('error');
          }
        }
      };

      ws.onerror = error => {
        console.error('[WebSocket] Connection error:', error);
        setError('Connection error occurred');
        setConnectionStatus('error');
        setIsConnecting(false);
      };
    } catch (error) {
      console.error('[WebSocket] Error creating connection:', error);
      setError('Failed to create connection');
      setConnectionStatus('error');
      setIsConnecting(false);
    }
  }, [token, user?.id, wsUrl, reconnectInterval, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    setConnectionStatus('disconnected');
    setError(null);
    reconnectAttemptsRef.current = 0;
  }, []);

  const sendMessage = useCallback(
    (message: Message) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const messageWithTimestamp = {
          ...message,
          sent_at: new Date().toISOString(),
          user_id: user?.id,
        };

        wsRef.current.send(JSON.stringify(messageWithTimestamp));
        //console.log('[WebSocket] Message sent:', messageWithTimestamp);
      } else {
        console.warn('[WebSocket] Cannot send message: Connection not open');
        setError('Cannot send message: Connection not open');
      }
    },
    [user?.id]
  );

  // Auto-connect when user logs in
  useEffect(() => {
    if (autoConnect && token && user?.id && !isConnected && !isConnecting) {
      //console.log('[WebSocket] Auto-connecting...');
      connect();
    }
  }, [autoConnect, token, user?.id, isConnected, isConnecting, connect]);

  // Disconnect when user logs out
  useEffect(() => {
    if (!token || !user?.id) {
      //console.log('[WebSocket] User logged out, disconnecting...');
      disconnect();
    }
  }, [token, user?.id, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const contextValue: WebSocketContextProps = {
    isConnected,
    isConnecting,
    lastMessage,
    sendMessage,
    connect,
    disconnect,
    connectionStatus,
    error,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocket = (): WebSocketContextProps => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
